<template>
  <actionable-panel
    :header-text="headerText"
    :complete="complete"
    :active="active"
    :current-step="currentStep"
    :total-steps="totalSteps"
    :disable-edit="editDisabled"
    :is-not-step3-wro-edit="!isWroCreated"
    @edit="transitionEdit"
  >
    <div class="wro-actionable-panel__body">
      <sb-field class="wro-actionable-panel__selection">
        <sb-label>Shipping Type:</sb-label>
        <div>
          <sb-radio-button
            v-for="(packagingType, index) in packagingTypes"
            :key="index"
            v-model="selectedPackagingType"
            :disabled="isWroCreated && selectedPackagingType !== packagingType"
            :value="packagingType"
            :data-testid="`shipping-type-option-${packagingType.id}`"
          >
            {{ $t(packagingType.name) }}
            <sb-field-helper-text v-if="packagingType.text">
              <p class="w-o__help-panel-text">
                {{ $t(packagingType.text) }}
              </p>
            </sb-field-helper-text>
          </sb-radio-button>
        </div>
      </sb-field>
    </div>
    <div
      v-if="
        hasSelectedAPackagingType && !floorLoadedContainerWithNoMultipleLots
      "
    >
      <div class="wro-actionable-panel__body">
        <sb-field class="wro-actionable-panel__selection">
          <sb-label>How is your inventory configured?</sb-label>
          <div>
            <sb-radio-button
              v-for="(skuType, index) in skuTypes"
              :key="index"
              v-model="selectedSKUType"
              :disabled="isWroCreated && selectedSKUType !== skuType"
              :value="skuType"
            >
              {{ setSKUContainerText(skuType.name.Name) }}
              <sb-field-helper-text v-if="skuType.name.Text">
                <p class="w-o__help-panel-text">
                  {{ setSKUContainerText(skuType.name.Text) }}
                </p>
              </sb-field-helper-text>
            </sb-radio-button>
          </div>
        </sb-field>
      </div>
    </div>
    <div v-if="canPurchaseLabels">
      <div class="wro-actionable-panel__body">
        <sb-field class="wro-actionable-panel__selection">
          <sb-label>Which labels will you be using?</sb-label>
          <div>
            <sb-radio-button
              v-for="(labelPurchaseType, index) in labelPurchaseTypes"
              :key="index"
              v-model="selectedLabelPurchaseType"
              :disabled="
                isWroCreated && selectedLabelPurchaseType !== labelPurchaseType
              "
              :value="labelPurchaseType"
            >
              {{ $t($t(labelPurchaseType.name).Name) }}
              <sb-badge
                v-if="labelPurchaseType.id === 1"
                class="purchase-label-badge"
                size="extra-small"
              >
                NEW!
              </sb-badge>
              <sb-field-helper-text v-if="$t(labelPurchaseType.name).Text">
                <p class="w-o__help-panel-text">
                  {{ $t($t(labelPurchaseType.name).Text) }}
                </p>
              </sb-field-helper-text>
              <origin-address
                v-if="showOriginAddress && labelPurchaseType.id === 1"
                v-model="originAddress"
              />
            </sb-radio-button>
          </div>
        </sb-field>
      </div>
    </div>
    <div
      v-if="(palletized || floorLoaded) && !isPoweredBySubAccountUser"
      slot="table"
      class="wro-warning-banner"
    >
      <sb-banner variation="warning">
        If you're sending pallets or floor loaded containers, you have to
        <a data-testid="link-schedule" :href="openDockUrl" target="_blank">
          schedule an appointment
        </a>
        at the FC. We will not accept inventory without appointments. Learn more
        <help-center-link link-key="createWro/freightAppointment">
          here
        </help-center-link>.
      </sb-banner>
    </div>
    <sb-panel
      v-if="(floorLoaded || multipleSKUSelected) && !isPoweredBySubAccountUser"
      slot="info"
      class="wro-actionable-panel__info-panel"
      type="sub"
    >
      <h4
        v-if="multipleSKUSelected"
        class="wro-actionable-panel__info-panel-title"
      >
        Did you know?
      </h4>
      <h4 v-else class="wro-actionable-panel__info-panel-title">
        Heads Up!
      </h4>

      <template v-if="multipleSKUSelected">
        <p class="wro-actionable-panel__info-panel-note">
          It is best to have only one SKU per box, as mixed boxes are usually
          more time-consuming. Each additional man-hour is charged at $40/hr
          after the initial 2 hours.
        </p>
        <p class="wro-actionable-panel__info-panel-note">
          Barcodes for each product are highly encouraged as it helps in
          reducing errors.
        </p>
      </template>

      <p v-if="floorLoaded" class="wro-actionable-panel__info-panel-note">
        Sending full, floor loaded (i.e. not palletized) containers can result
        in additional fees due to the high labor costs. If your product is
        palletized within a container, select the “Palletized container” option.
      </p>
    </sb-panel>
    <div slot="footer" class="wro-actionable-panel__footer">
      <sb-async-button
        :loading-promise="savingWro"
        :disabled="disableSave"
        class="wro-save-button wro-actionable-panel__action"
        data-testid="save-and-continue"
        @click="validateAddressAndSave"
      >
        {{ isWroCreated ? "Continue" : "Save & Continue" }}
      </sb-async-button>
      <a
        v-if="!isWroCreated"
        class="wro-actionable-panel__action"
        href="#"
        @click.prevent="transitionClear"
      >
        Cancel
      </a>
    </div>
  </actionable-panel>
</template>

<script>
import stepsMixin from './stepsMixin'
import PackagingTypesEnum from '@enums/modules/creatingWarehouseReceivingOrder/packagingType'
import SKUTypesEnum from '@enums/modules/creatingWarehouseReceivingOrder/skuType'
import OriginAddress from '../helpers/OriginAddress.vue'
import ServiceTypes from '@/enums/modules/fulfillmentCenter/serviceType.js'
import toastr from 'toastr'

export default {
  name: 'HowWillInventoryArrive',
  inject: ['isPoweredBySubAccountUser'],
  components: { OriginAddress },
  mixins: [stepsMixin],
  props: {},
  data () {
    return {
      PackagingTypesEnum,
      skuTypes: [
        { id: 1, name: this.$t(SKUTypesEnum.OneSKUPerContainer) },
        { id: 2, name: this.$t(SKUTypesEnum.MultipleSKUPerContainer) }
      ],
      labelPurchaseTypes: PackagingTypesEnum.labelPurchaseTypes,
      openDockUrl: null,
      wroParcelBox: null
    }
  },
  computed: {
    headerText () {
      return `How will your inventory arrive to ${this.isPoweredBySubAccountUser ? 'our' : 'ShipBob\'s'} fulfillment center?`
    },
    packagingTypes () {
      return [
        { id: 1, name: this.hasWroParcelBoxLimit ? `${this.$t('packagingType.Parcel.Name')} Limit of ${this.wroParcelBox} boxes` : this.$t('packagingType.Parcel.Name'), text: this.$t('packagingType.Parcel.Text') },
        { id: 2, name: this.$t('packagingType.PalletizedContainerOrLTL.Name'), text: this.$t('packagingType.PalletizedContainerOrLTL.Text') },
        { id: 3, name: this.$t('packagingType.FloorLoadedContainer.Name'), text: this.$t('packagingType.FloorLoadedContainer.Text') }
      ]
    },
    hasSelectedAPackagingType () {
      return this.selectedPackagingType && this.selectedPackagingType.id !== 0
    },
    hasSelectedASKUType () {
      return this.selectedSKUType && this.selectedSKUType.id !== 0
    },
    showOriginAddress () {
      return (
        this.selectedLabelPurchaseType &&
        this.selectedLabelPurchaseType.id === 1
      )
    },
    notParcel () {
      return (
        this.hasSelectedAPackagingType && this.selectedPackagingType.id !== 1
      )
    },
    canPurchaseLabels () {
      const receivingService =
        this.draft &&
        this.draft.fulfillmentCenter &&
        this.draft.fulfillmentCenter.getService(ServiceTypes.Receiving)
      const fulfillmentCenterAddress =
        receivingService && receivingService.address
      return (
        this.wroLabelPurchaseFlag &&
        this.hasSelectedAPackagingType &&
        this.selectedPackagingType.id === 1 &&
        !this.isDangerousGoods &&
        fulfillmentCenterAddress.country.toUpperCase() === 'US'
      )
    },
    isDangerousGoods () {
      return this.$shipBobStore.getters[
        this.StoreModuleName + '/selectedProducts'
      ].some((x) => x.isDangerousGoods === true)
    },
    palletized () {
      return (
        this.hasSelectedAPackagingType && this.selectedPackagingType.id === 2
      )
    },
    floorLoaded () {
      return (
        this.hasSelectedAPackagingType && this.selectedPackagingType.id === 3
      )
    },
    multipleSKUSelected () {
      return this.hasSelectedASKUType && this.selectedSKUType.id === 2
    },
    floorLoadedContainerWithNoMultipleLots () {
      return this.$shipBobStore.getters[
        this.StoreModuleName + '/floorLoadedContainerWithNoMultipleLots'
      ]
    },
    originAddress () {
      return this.$shipBobStore.getters[
        this.StoreModuleName + '/originAddress'
      ]
    },
    selectedPackagingType: {
      get () {
        const type =
          this.$shipBobStore.getters[
            this.StoreModuleName + '/selectedPackagingType'
          ]
        if (!type) return
        return this.packagingTypes.find((t) => type.id === t.id)
      },
      set (newVal) {
        this.$shipBobStore.dispatch(
          this.StoreModuleName + '/setSelectedPackagingType',
          newVal
        )
        if (!this.canPurchaseLabels) {
          this.machine.transition('NO_LABEL_OPTION_NEEDED')
        } else {
          this.machine.transition('PACKAGE_TYPE_SELECTED')
        }
      }
    },
    selectedSKUType: {
      get () {
        const type =
          this.$shipBobStore.getters[this.StoreModuleName + '/selectedSKUType']
        if (!type) return
        return this.skuTypes.find((t) => type.id === t.id)
      },
      set (newVal) {
        this.$shipBobStore.dispatch(
          this.StoreModuleName + '/setSelectedSKUType',
          newVal
        )
        this.transitionInput()
      }
    },
    selectedLabelPurchaseType: {
      get () {
        const type =
          this.$shipBobStore.getters[
            this.StoreModuleName + '/selectedLabelPurchaseType'
          ]
        if (!type) return
        return this.labelPurchaseTypes.find((t) => type.id === t.id)
      },
      set (newVal) {
        this.$shipBobStore.dispatch(
          this.StoreModuleName + '/setSelectedLabelPurchaseType',
          newVal
        )
        this.machine.transition('LABEL_TYPE_SELECTED')
      }
    }
  },
  watch: {
    selectedPackagingType: {
      handler (n, o) {
        this.machine.transition('INPUT')
      }
    },
    originAddress: {
      handler (n, o) {
        this.machine.transition('ADDRESS_UPDATED')
      }
    }
  },
  async mounted () {
    await this.getSystemSetting('OpenDockUrlWRO', 'openDockUrl')
    await this.getSystemSetting('WroParcelBoxLimit', 'wroParcelBox')
  },
  methods: {
    setSKUContainerText (template) {
      const type = this.palletized ? 'pallet' : 'box'
      return this.$t(template.replace('{container}', type))
    },
    async validateAddressAndSave () {
      if (this.canPurchaseLabels && this.selectedLabelPurchaseType.id === 1) {
        const response = await this.$shipBobStore.dispatch(
          this.StoreModuleName + '/validateAddressAndSave'
        )
        if (
          response.success &&
          response.response.isDomestic &&
          response.response.isValid
        ) {
          this.transitionSave()
        } else {
          toastr.error('Invalid address entered.')
        }
      } else {
        this.transitionSave()
      }
    }
  },
  i18n: { messages: {} }
}
</script>

<style scoped lang="scss">
@import "./panel.scss";

.wro-warning-banner {
  margin: 0 2em 1em 2em;
}

.wro-warning-banner a {
  text-decoration: none;
}

.purchase-label-badge {
  margin-left: 0.75rem;
}

.w-o__help-panel-text {
  font-weight: 400;
  color: #a9acb3;
}
</style>
